import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Phone, AtSign, CheckCircle, Mail } from 'lucide-react';

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { user, completeProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    phone: user?.phone || '',
    first_name: user?.first_name || user?.name?.split(' ')[0] || '',
    last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.username.trim()) {
      toast.error('Username is required');
      return;
    }
    if (formData.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      await completeProfile({
        username: formData.username.trim(),
        phone: formData.phone.trim(),
        first_name: formData.first_name.trim() || undefined,
        last_name: formData.last_name.trim() || undefined,
      });
      toast.success('Profile completed successfully!');
      navigate('/rider');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if profile is already complete
  if (user?.profile_complete !== false) {
    navigate('/rider');
    return null;
  }

  return (
    <div className="min-h-screen bg-dark-bg text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-neon-cyan/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-neon-cyan" />
          </div>
          <h1 className="font-outfit font-bold text-3xl mb-2">Almost There!</h1>
          <p className="text-white/60">Complete your profile to start using vgoride</p>
        </div>

        {/* User Info from Social Login */}
        {user && (
          <div className="bg-dark-surface/50 rounded-xl p-4 mb-6 border border-white/10">
            <div className="flex items-center gap-3">
              {user.profile_picture ? (
                <img 
                  src={user.profile_picture} 
                  alt="Profile" 
                  className="w-12 h-12 rounded-full"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-neon-cyan/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-neon-cyan" />
                </div>
              )}
              <div>
                <div className="font-medium text-white">{user.name || 'User'}</div>
                <div className="text-sm text-white/60 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {user.email}
                </div>
              </div>
              <div className="ml-auto">
                <span className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded-full capitalize">
                  via {user.auth_provider || 'Google'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-dark-surface rounded-2xl p-6 border border-white/5">
          <div className="space-y-5">
            {/* First Name */}
            <div>
              <Label htmlFor="first_name" className="text-white/80 mb-2 block">
                First Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                  placeholder="Your first name"
                  data-testid="input-first-name"
                />
              </div>
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="last_name" className="text-white/80 mb-2 block">
                Last Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                  placeholder="Your last name"
                  data-testid="input-last-name"
                />
              </div>
            </div>

            {/* Username */}
            <div>
              <Label htmlFor="username" className="text-white/80 mb-2 block">
                Username <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                  placeholder="Choose a unique username"
                  required
                  data-testid="input-username"
                />
              </div>
              <p className="text-xs text-white/40 mt-1">Letters, numbers, and underscores only</p>
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-white/80 mb-2 block">
                Phone Number <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9+\-\s]/g, '') })}
                  className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                  placeholder="Your phone number"
                  required
                  data-testid="input-phone"
                />
              </div>
              <p className="text-xs text-white/40 mt-1">We will use this to contact you about your rides</p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 mt-8"
            data-testid="complete-profile-btn"
          >
            {loading ? 'Completing Profile...' : 'Complete Profile & Start Riding'}
          </button>
        </form>

        {/* Support */}
        <div className="mt-6 text-center">
          <p className="text-white/40 text-sm">Need help?</p>
          <a 
            href="mailto:customer-support@vgoride.net" 
            className="text-neon-cyan hover:text-white transition-colors text-sm"
          >
            customer-support@vgoride.net
          </a>
        </div>
      </div>
    </div>
  );
}
